<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { useToast } from 'primevue/usetoast';
import { ClinicianInterface } from '@/types';
import { useStudentClinicianStore } from '@/store';
import { EMIT, PERMISSIONS } from '@/enum';
import { can, isValidForRoleByTenant, usePersonTypes } from '@/composables';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';

interface ClinicianPropsInterface {
  tableData?: ClinicianInterface[];
  studentId?: number | undefined;
  loading?: boolean;
}

interface ClinicianTableInterface {
  id: number;
  display_name: string;
  disciplines?: string[];
}

const props = defineProps<ClinicianPropsInterface>();
const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();
const clinicianStudentStore = useStudentClinicianStore();
const clinicians: Ref<ClinicianInterface[] | undefined> = toRef(props, 'tableData');
const clinicianCount: Ref<number> = computed(() => (clinicians.value ? clinicians.value.length : 0));
const clinicianModalOpen = ref(false);
const toast = useToast();
const clinicianItems: Ref<{ name: string; value: number }[]> = ref([]);

const createMenuModel = (item: ClinicianTableInterface) => {
  const menuItem = {
    label: 'Remove service provider',
    command: () => {
      detachClinician([item.id], item.display_name);
    },
    visible: can([PERMISSIONS.STUDENT_DETACH_CLINICIANS]),
  };
  return [menuItem].filter((item) => item.visible);
};

const attachClinician: (ids: number[]) => Promise<void> = async (ids) => {
  await clinicianStudentStore.attachClinicians({
    clinicians: ids,
    studentId: props.studentId as number,
  });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Added',
    detail: 'Service Provider(s) has been added',
    life: 3000,
  });
};

const detachClinician: (id: number[], clinicianName: string) => Promise<void> = async (ids, clinicianName) => {
  await clinicianStudentStore.detachClinicians({ clinicians: ids, studentId: props.studentId as number });
  emit(EMIT.UPDATE);
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail: `Service Provider ${clinicianName} has been removed from student`,
    life: 3000,
  });
};

const openAddClinicians = async () => {
  if (can([PERMISSIONS.STUDENT_ATTACH_CLINICIANS]) && can([PERMISSIONS.STUDENT_CLINICIANS_AVAILABLE_TO_ADD])) {
    clinicianItems.value = await clinicianStudentStore.getAddCliniciansModalData(props.studentId as number);
    clinicianModalOpen.value = true;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add service providers',
      life: 3000,
    });
  }
};

const onCancelAdd = () => (clinicianModalOpen.value = false);

const onSaveAdd = (clinicians: { name: string; value: number }[]) => {
  const cliniciansId = clinicians.map((clinicians) => clinicians.value);
  attachClinician(cliniciansId);
  clinicianModalOpen.value = false;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Service Providers</h2>
      <Chip>{{ clinicianCount }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.STUDENT_ATTACH_CLINICIANS]) && isValidForRoleByTenant()"
          type="text"
          size="small"
          text="Add Service Provider"
          icon-left="add"
          @click="openAddClinicians"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="clinicians"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-student-clinician-store-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="clinicianCount">
          <Column body-class="mw-20rem" header-class="mw-20rem" field="name" header="Service Provider">
            <template #body="{ data }">
              <RouterLink
                v-if="isValidForRoleByTenant(data.people_type) && !usePersonTypes.isCaseManager()"
                :to="`/user/${data.id}`"
                class="font-semibold"
                v-text="data.full_name"
              />
              <span v-else class="font-semibold" v-text="data.full_name" />
            </template>
          </Column>
          <Column field="disciplines" header="Disciplines">
            <template #body="{ data }">
              <div v-if="data.disciplines">
                <span v-for="(discipline, index) in data.disciplines" :key="discipline">
                  <span>{{ discipline.name }}</span>
                  <span v-if="index < data.disciplines.length - 1">,&nbsp;</span>
                </span>
              </div>
            </template>
          </Column>
          <Column
            body-style="justify-content: center; max-width: 10rem;"
            header-style="justify-content: center; max-width: 10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="createMenuModel(data).length > 0"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Service Providers not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="clinicianModalOpen && can([PERMISSIONS.STUDENT_CLINICIANS_AVAILABLE_TO_ADD]) && isValidForRoleByTenant()"
        title="Add Service Providers"
        :items="clinicianItems"
        :searchable="true"
        button-text="Add Service Providers"
        placeholder="Search Service Providers"
        person-type="clinician"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
:deep(.p-card) {
  min-width: calc(100% - 1.5rem);
}

.clinician-link {
  font-weight: 600;
  color: var(--primary-color);
}
</style>
